import {
    RequestMethod,
    RequestStatus,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { ParkingPortalLayoutWithHeader } from '../mobile/layout/ParkingPortalLayoutWithHeader.tsx';
import { Localized, useTranslation } from 'dg-web-shared/lib/Localization.tsx';
import { Box, Button, InputLabel, Stack, Switch } from '@mui/material';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useOffstreetEventCardsOutlet } from './OperatorOffstreetEventCards.tsx';
import { ReactHookFormTextField } from 'dg-web-shared/common/components/material-ui/react-hook-form-fields/ReactHookFormTextField.tsx';
import { useForm } from 'react-hook-form';
import {
    MAX_EVENT_STACK_CONTRACTS,
    OffstreetEventStackErrorResonse,
    OperatorOffstreetEventsAddStackErrorModal,
} from './OperatorOffstreetEventsAddStack.tsx';
import {
    mandatoryFieldErrorMessage,
    maxFieldErrorMessage,
    tooManyCharsMessage,
} from 'dg-web-shared/common/components/translations/FormValidationErrorTexts.ts';
import { useState } from 'react';
import { ReadOnlyTextField } from '../ui/material-ui/ReadOnlyTextField.tsx';
import { SubmitButton } from '../ui/buttons/SubmitButton.tsx';

interface EditEventStackPayload {
    eventId: string;
    stackId: string;
    stackName: string;
    additionalContractsCount: number;
}

export interface EditEventStackValues {
    [EditEventStackFields.stackName]: string;
    [EditEventStackFields.additionalContractsCount]: number;
}

export const enum EditEventStackFields {
    stackName = 'stackName',
    additionalContractsCount = 'additionalContractsCount',
}

export function OperatorOffstreetEventEditStack() {
    const { stackId } = useParams();
    const {
        event: { eventId, stacks },
        refetchEvent,
    } = useOffstreetEventCardsOutlet();
    const stack = stacks.find(stack => stack.stackId === stackId);
    const [editStackState, editStack] = useServerWrite<
        EditEventStackPayload,
        null,
        OffstreetEventStackErrorResonse
    >(({ stackId, eventId }) => ({
        url: `/ui-api/operator-account/offstreet-events/${eventId}/stack/${stackId}/edit`,
        method: RequestMethod.PUT,
    }));
    const navigate = useNavigate();
    useServerSuccessEffect(editStackState, () => {
        refetchEvent();
        navigate('..');
    });
    const [addAdditionalContract, setAddAdditionalContract] = useState(false);

    const { control, handleSubmit, setValue } = useForm<EditEventStackValues>({
        defaultValues: {
            [EditEventStackFields.stackName]: stack?.stackName,
            [EditEventStackFields.additionalContractsCount]: 0,
        },
        mode: 'onBlur',
    });

    const maxLengthDescription = 30;
    const descriptionTooLong = useTranslation(
        tooManyCharsMessage(maxLengthDescription),
    );
    const maxNumberOfEventContracts =
        MAX_EVENT_STACK_CONTRACTS - (stack?.count ?? 0);
    const maximumMessage = useTranslation(
        maxFieldErrorMessage(maxNumberOfEventContracts),
    );
    const mandatoryFieldMessage = useTranslation(mandatoryFieldErrorMessage);
    const editPending = editStackState.status == RequestStatus.PENDING;

    if (!stack) {
        return <Navigate to={'..'} />;
    }
    return (
        <OperatorRoutedModal
            variant={ModalVariant.TINY}
            backUrl={'..'}
            render={() => (
                <ParkingPortalLayoutWithHeader
                    title={
                        <Localized
                            de="Stapel editieren"
                            fr="Modifier le set"
                            it="Modifica set"
                            en="Edit stack"
                        />
                    }
                >
                    <Box
                        component="form"
                        onSubmit={handleSubmit((data: EditEventStackValues) => {
                            editStack({
                                ...data,
                                eventId,
                                stackId: stack.stackId,
                            });
                        })}
                        sx={{
                            padding: '25px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                        }}
                    >
                        <OperatorOffstreetEventsAddStackErrorModal
                            OffstreetEventStackState={editStackState}
                        />

                        <ReactHookFormTextField
                            label={
                                <Localized
                                    de="Bezeichnung"
                                    fr="Désignation"
                                    it="Descrizione"
                                    en="Description"
                                />
                            }
                            name={EditEventStackFields.stackName}
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: mandatoryFieldMessage,
                                },
                                maxLength: {
                                    value: maxLengthDescription,
                                    message: descriptionTooLong,
                                },
                                minLength: {
                                    value: 1,
                                    message: mandatoryFieldMessage,
                                },
                            }}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Aktuelle Anzahl"
                                    fr="Quantité actuelle"
                                    it="Quantità attuale"
                                    en="Current quantity"
                                />
                            }
                            value={stack.count}
                        />
                        <Box>
                            <InputLabel
                                sx={{ lineHeight: '1em', paddingTop: '1px' }}
                            >
                                <Localized
                                    de="Zusätzliche Anzahl"
                                    fr="Quantité supplémentaire"
                                    it="Quantità aggiuntiva"
                                    en="Additional quantity"
                                />
                            </InputLabel>
                            <Stack
                                direction={'row'}
                                sx={{
                                    fontWeight: '500',
                                    fontSize: '16px',
                                    paddingBottom: '5px',
                                    gap: '12px',
                                }}
                            >
                                <Switch
                                    checked={addAdditionalContract}
                                    onChange={(_e, checked) => {
                                        if (checked) {
                                            setValue(
                                                EditEventStackFields.additionalContractsCount,
                                                1,
                                            );
                                            setAddAdditionalContract(true);
                                        } else {
                                            setValue(
                                                EditEventStackFields.additionalContractsCount,
                                                0,
                                            );
                                            setAddAdditionalContract(false);
                                        }
                                    }}
                                    disabled={
                                        stack.count >= MAX_EVENT_STACK_CONTRACTS
                                    }
                                />
                                {addAdditionalContract && (
                                    <ReactHookFormTextField
                                        type="number"
                                        name={
                                            EditEventStackFields.additionalContractsCount
                                        }
                                        control={control}
                                        rules={{
                                            max: {
                                                value: maxNumberOfEventContracts,
                                                message: maximumMessage,
                                            },
                                            required: {
                                                value: true,
                                                message: mandatoryFieldMessage,
                                            },
                                        }}
                                        sx={{
                                            paddingBottom: '25px',
                                            width: '150px',
                                        }}
                                    />
                                )}
                            </Stack>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                paddingTop: '8px',
                            }}
                        >
                            <Button
                                onClick={() => navigate('..')}
                                variant="outlined"
                                disabled={editPending}
                            >
                                <Localized
                                    de="Abbrechen"
                                    fr="Annuler"
                                    it="Annulla"
                                    en="Cancel"
                                />
                            </Button>
                            <SubmitButton
                                sx={{ marginLeft: '5px' }}
                                loading={editPending}
                                label={
                                    <Localized
                                        de="Speichern"
                                        fr="Sauvegarder"
                                        it="Salva"
                                        en="Save"
                                    />
                                }
                            />
                        </Box>
                    </Box>
                </ParkingPortalLayoutWithHeader>
            )}
        />
    );
}
